import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import { selfRegisterOrganization } from "../../setup/api/services/organization";
import TextInput from "../common/hookForm/TextInput";
import TextArea from "../common/hookForm/TextArea";
import PrimaryButton from "../common/PrimaryButton";

// TextInput requires onBlur; these fields have no blur behaviour.
const noop = () => undefined;

/** What the gate needs to render its shell for a just-submitted org, before any refetch. */
export interface ISubmittedOrganization {
  /** Returned by the backend (it generates the code); required to attach onboarding documents. */
  code: string;
  name: string;
  logoUrl?: string;
}

interface Props {
  onSubmitted: (organization: ISubmittedOrganization) => void;
  /** Pre-fills the form for a resubmission after rejection. */
  initialValues?: Partial<IFormValues>;
}

// Same three fields as the Waffy ID self-registration page. The organization code is generated
// by the backend and the logo is uploaded later from the sidebar.
interface IFormValues {
  name: string;
  websiteUrl: string;
  description: string;
}

const EMPTY_VALUES: IFormValues = {
  name: "",
  websiteUrl: "",
  description: "",
};

const WEBSITE_URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]+$/i;

// Known backend business-rule messages (see OrganizationServiceImpl.selfRegister /
// resubmitRejectedOrganization in authentication-service), mapped to a meaningful Arabic
// message instead of the generic "something went wrong" fallback.
const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  "code already exists": "هذا الرمز مستخدم بالفعل، الرجاء اختيار رمز آخر",
  "You already belong to an organization": "أنت مسجل بالفعل في مؤسسة",
  "Organization code cannot be changed on resubmission":
    "لا يمكن تغيير رمز المؤسسة عند إعادة الإرسال",
};

const CreateOrganizationForm: React.FC<Props> = ({
  onSubmitted,
  initialValues,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<IFormValues>({
    name: initialValues?.name ?? EMPTY_VALUES.name,
    websiteUrl: initialValues?.websiteUrl ?? EMPTY_VALUES.websiteUrl,
    description: initialValues?.description ?? EMPTY_VALUES.description,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField =
    (field: keyof IFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  // The website is optional, but when it is filled in it must be a valid address.
  const isWebsiteValid = useMemo(
    () =>
      values.websiteUrl.trim() === "" ||
      WEBSITE_URL_PATTERN.test(values.websiteUrl.trim()),
    [values.websiteUrl]
  );

  const canSubmit = values.name.trim() !== "" && isWebsiteValid;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(
        !isWebsiteValid
          ? t("lbl_org_field_website_invalid")
          : t("lbl_org_required_field")
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const name = values.name.trim();
      const response = await selfRegisterOrganization({
        name,
        websiteUrl: values.websiteUrl.trim() || undefined,
        description: values.description.trim() || undefined,
      });
      // The backend generates the code (or keeps the existing one on a resubmission).
      onSubmitted({
        code: response?.data?.code ?? "",
        name,
        logoUrl: response?.data?.logoUrl || undefined,
      });
    } catch (err: any) {
      const backendMessage = err?.error?.message;
      setError(
        (backendMessage && KNOWN_ERROR_MESSAGES[backendMessage]) ||
          t("lbl_org_submit_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div>
        <h2 className={styles.title}>{t("lbl_org_onboarding_title")}</h2>
        <p className={styles.subtitle}>{t("lbl_org_onboarding_subtitle")}</p>
      </div>
      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <TextInput
            name="name"
            value={values.name}
            onChange={setField("name")}
            onBlur={noop}
            placeholder={`${t("lbl_org_field_name")} *`}
          />
        </div>
        <div className={styles.field}>
          <TextInput
            name="websiteUrl"
            type="url"
            dir="ltr"
            value={values.websiteUrl}
            onChange={setField("websiteUrl")}
            onBlur={noop}
            placeholder={t("lbl_org_field_website_optional") as string}
            hint="https://example.com"
          />
        </div>
        <div className={styles.field}>
          <TextArea
            name="description"
            value={values.description}
            onChange={setField("description")}
            label={t("lbl_org_field_description") as string}
            showLabel
            showMaxLength={false}
          />
        </div>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.submitRow}>
          <PrimaryButton type="submit" disabled={submitting || !canSubmit}>
            {t("lbl_org_submit")}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default CreateOrganizationForm;
